import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  FileText,
  Globe2,
  MessageCircle,
  X,
} from 'lucide-react';
import { useLocale } from '../../hooks/useLocale';

type PostOnboardingWelcomeContentProps = {
  visible?: boolean;
  onContinue: () => void;
  onClose?: () => void;
};

const FEATURES = [
  { key: 'chatApply', icon: MessageCircle },
  { key: 'tailoredCv', icon: FileText },
  { key: 'webCv', icon: Globe2 },
  { key: 'jobMatches', icon: Bell },
] as const;

export function PostOnboardingWelcomeContent({
  visible = true,
  onContinue,
  onClose,
}: PostOnboardingWelcomeContentProps) {
  const { t } = useTranslation('seeker');
  const { isRTL } = useLocale();
  const close = onClose ?? onContinue;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close, visible]);

  if (!visible || !mounted || typeof document === 'undefined') {
    return null;
  }

  const dialog = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-onboarding-welcome-title"
      className="fixed inset-0 flex items-center justify-center"
      style={{
        direction: isRTL ? 'rtl' : 'ltr',
        padding: 18,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: 9999,
      }}
    >
      <button
        type="button"
        aria-label={t('chat.postOnboardingWelcome.close')}
        onClick={close}
        className="absolute inset-0 h-full w-full cursor-default border-0 bg-transparent p-0"
      />

      <section
        className="post-onboarding-welcome-dialog relative flex w-full flex-col overflow-hidden"
        style={{
          maxWidth: 520,
          maxHeight: 'calc(100vh - 36px)',
          borderRadius: 28,
          backgroundColor: 'var(--tabbio-bg)',
          color: 'var(--tabbio-text)',
          boxShadow: '0 24px 70px rgba(0, 0, 0, 0.22)',
        }}
      >
        <button
          type="button"
          aria-label={t('chat.postOnboardingWelcome.close')}
          onClick={close}
          className="absolute top-4 flex h-9 w-9 items-center justify-center border-0 bg-transparent"
          style={{
            insetInlineEnd: 18,
            borderRadius: 12,
            color: 'var(--tabbio-text-tertiary)',
            cursor: 'pointer',
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.backgroundColor = 'var(--tabbio-surface-secondary)';
            event.currentTarget.style.color = 'var(--tabbio-text)';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.backgroundColor = 'transparent';
            event.currentTarget.style.color = 'var(--tabbio-text-tertiary)';
          }}
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        <div
          className="post-onboarding-welcome-scroll flex-1 overflow-y-auto"
          style={{ padding: '46px 42px 22px' }}
        >
          <h1
            id="post-onboarding-welcome-title"
            className="post-onboarding-welcome-title"
            style={{
              margin: 0,
              color: 'var(--tabbio-text)',
              fontSize: 42,
              lineHeight: '48px',
              fontWeight: 680,
              letterSpacing: 0,
              maxWidth: 380,
            }}
          >
            {t('chat.postOnboardingWelcome.title')}
          </h1>
          <p
            style={{
              margin: '18px 0 0',
              color: 'var(--tabbio-text-secondary)',
              fontSize: 18,
              lineHeight: '29px',
              maxWidth: 430,
            }}
          >
            {t('chat.postOnboardingWelcome.subtitle')}
          </p>

          <div
            className="post-onboarding-welcome-features"
            style={{ display: 'flex', flexDirection: 'column', gap: 26, marginTop: 34 }}
          >
            {FEATURES.map(({ key, icon: Icon }) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 20,
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                }}
              >
                <div
                  style={{
                    width: 42,
                    minHeight: 42,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    paddingTop: 2,
                    flexShrink: 0,
                  }}
                >
                  <Icon size={30} color="var(--tabbio-text)" strokeWidth={2.4} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2
                    style={{
                      margin: 0,
                      color: 'var(--tabbio-text)',
                      fontSize: 18,
                      lineHeight: '24px',
                      fontWeight: 680,
                      letterSpacing: 0,
                    }}
                  >
                    {t(`chat.postOnboardingWelcome.features.${key}.title`)}
                  </h2>
                  <p
                    style={{
                      margin: '4px 0 0',
                      color: 'var(--tabbio-text-secondary)',
                      fontSize: 16,
                      lineHeight: '25px',
                    }}
                  >
                    {t(`chat.postOnboardingWelcome.features.${key}.body`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '10px 42px 24px' }}>
          <button
            type="button"
            onClick={onContinue}
            style={{
              width: '100%',
              minHeight: 58,
              borderRadius: 999,
              border: 0,
              backgroundColor: 'var(--tabbio-text)',
              color: 'var(--tabbio-bg)',
              font: 'inherit',
              fontSize: 17,
              fontWeight: 680,
              cursor: 'pointer',
            }}
            onMouseDown={(event) => {
              event.currentTarget.style.transform = 'scale(0.985)';
            }}
            onMouseUp={(event) => {
              event.currentTarget.style.transform = 'scale(1)';
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {t('chat.postOnboardingWelcome.continueCta')}
          </button>
        </div>

        <style>{`
          .post-onboarding-welcome-dialog {
            animation: postOnboardingWelcomeIn 180ms ease-out;
          }

          @keyframes postOnboardingWelcomeIn {
            from {
              opacity: 0;
              transform: translateY(8px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @media (max-width: 520px) {
            .post-onboarding-welcome-dialog {
              border-radius: 24px;
            }

            .post-onboarding-welcome-scroll {
              padding: 42px 28px 20px !important;
            }

            .post-onboarding-welcome-title {
              font-size: 36px !important;
              line-height: 42px !important;
            }
          }
        `}</style>
      </section>
    </div>
  );

  return createPortal(dialog, document.body);
}
